import {
  Badge,
  cn,
  Icon,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@nexus/ui";
import type { PersonaHookVariationSuggestion } from "@nexus/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ScriptCustomAssetRowLink } from "@/features/marketplace/components/ScriptCustomAssetRowLink";
import { isCustomAssetScript } from "@/features/marketplace/lib/custom-asset";
import { ScriptSentToVendorCard } from "@/features/opportunities/components/scripts/ScriptSentToVendorCard/ScriptSentToVendorCard.js";
import { resolveOriginalBriefUrl } from "@/features/opportunities/lib/original-brief-url";
import { isSentToAnyPartner } from "@/features/marketplace/lib/sent-scripts-map";
import type { SentScriptMeta, SentScriptsMap } from "@/features/marketplace/types/marketplace.types";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import { ScriptPlatformBadge } from "@/features/persona-opportunities/components/platform/ScriptPlatformBadge";
import { canShowAddThreeHooks, shouldOpenHookVariationsPanel } from "@/features/persona-opportunities/lib/hook-variations-eligibility";
import {
  getHookVariantDisplayMeta,
  isPersonaScriptHookVariant,
} from "@/features/persona-opportunities/lib/hook-variations-order";
import { formatScriptCreatedAt, scriptMetaLine } from "@/features/persona-opportunities/lib/persona-script-meta";
import { personaScriptReferenceDetails } from "@/features/persona-opportunities/lib/persona-script-reference-details";
import { scriptDocxLabels } from "@/features/persona-opportunities/lib/persona-script-docx";
import { getPersonaScriptDisplayTitle } from "@/features/persona-opportunities/lib/script-display-title";
import {
  personaOpportunitySetBriefsQueryOptions,
  personaOpportunitySetScriptsQueryOptions,
  useAiEditPersonaScripts,
  useConfirmPersonaHookVariations,
  usePatchPersonaScriptContent,
  useSuggestPersonaHookVariations,
  useTogglePersonaScriptFavorite,
} from "@/lib/queries";

import { ScriptBroadcastSource } from "../../shared/ScriptBroadcastSource/ScriptBroadcastSource.js";
import { PersonaAiScriptEditorModal } from "../../workspace/tabs/ScriptsTab/PersonaAiScriptEditorModal.js";
import { PERSONA_SCRIPT_CELL_FLASH_MS } from "../../workspace/tabs/ScriptsTab/PersonaScriptEditableCell.js";
import {
  PersonaScriptAddThreeHooksButton,
  PersonaScriptBusyStatus,
} from "../../workspace/tabs/ScriptsTab/PersonaScriptActionsFooter.js";
import { PersonaScriptExpandedBody } from "../../workspace/tabs/ScriptsTab/PersonaScriptExpandedBody.js";
import { usePersonaScriptClipboard } from "../../workspace/tabs/ScriptsTab/usePersonaScriptClipboard.js";
import {
  parsePersonaScriptSceneCellKey,
  type PersonaScriptSceneField,
} from "../../workspace/tabs/ScriptsTab/PersonaScriptSceneTable.js";
import { PersonaScriptTitleEditor } from "../../workspace/tabs/ScriptsTab/PersonaScriptTitleEditor.js";
import { PersonaSendToVendorDialog } from "../../workspace/tabs/ScriptsTab/PersonaSendToVendorDialog/PersonaSendToVendorDialog.js";
import { ScriptsHistoryPanel } from "../../workspace/tabs/ScriptsTab/ScriptsHistoryDrawer.js";
import { changedSceneCellKeys } from "../../workspace/tabs/ScriptsTab/persona-script-version-highlight.js";

import { marketplaceOrderHref, useOpenMarketplaceOrder } from "./hub-order-link.js";
import { OpportunityHubBriefDocument } from "./OpportunityHubBriefDocument.js";
import { OpportunityHubDrawerToolbar } from "./OpportunityHubDrawerToolbar.js";
import { HUB_DRAWER_DOCKED_CLASS, shouldKeepHubDrawerOpen } from "./OpportunityHubOpportunityDrawer.js";
import type { HubChromeScriptRow } from "./hub-chrome.types.js";

/** Close control is 28px; center it in the 64px header row. */
const HEADER_CLOSE_CLASS = "top-[18px]";

function PastVersionHeading({
  preview,
  siblings,
  createdAt,
}: {
  preview: PersonaScriptDto;
  siblings: PersonaScriptDto[];
  /** `versions[N].createdAt` from the history rail, not the live script row. */
  createdAt: string | null;
}) {
  const { t, i18n } = useTranslation();
  const untitled = t("personaOpportunities.scripts.untitledHook", {
    defaultValue: "Script {{slot}}",
    slot: preview.slotIndex,
  });
  const title = getPersonaScriptDisplayTitle(preview, untitled);
  const variantMeta = getHookVariantDisplayMeta(preview, siblings);
  const createdAtLabel = createdAt ? formatScriptCreatedAt(createdAt, i18n.language) : "";

  return (
    <div className="mb-4 flex flex-col gap-3">
      <p className="text-drawer-title break-words text-foreground">{title}</p>
      {isPersonaScriptHookVariant(preview) ? (
        <Badge variant="ai" iconStart="sparkles" className="w-fit whitespace-normal text-left">
          {preview.hookStrategy?.trim()
            ? t("personaOpportunities.scripts.hookVariantBadge", {
                defaultValue: "{{strategy}} · HOOK VARIANT OF S{{slot}}",
                strategy: preview.hookStrategy.trim(),
                slot: variantMeta?.parentSlot ?? preview.slotIndex,
              })
            : t("personaOpportunities.scripts.hookVariantBadgeFallback", {
                defaultValue: "HOOK VARIANT OF S{{slot}}",
                slot: variantMeta?.parentSlot ?? preview.slotIndex,
              })}
        </Badge>
      ) : null}
      <p className="flex flex-wrap items-center gap-x-2 text-caption tabular-nums text-muted-foreground">
        <span>
          {scriptMetaLine(
            {
              slotIndex: preview.slotIndex,
              sceneCount: preview.scenes.length,
              duration: preview.duration,
            },
            t,
            variantMeta,
          )}
        </span>
        {createdAtLabel ? (
          <span className="inline-flex items-center gap-1">
            <Icon name="clock" size="xs" color="muted" aria-hidden />
            {createdAtLabel}
          </span>
        ) : null}
      </p>
    </div>
  );
}

function HubScriptDocument({
  script,
  siblings,
  setId,
  tenantId,
  canEdit,
  sentMeta,
  opportunityAngle,
  sheetTitle,
  setName,
}: {
  script: PersonaScriptDto;
  siblings: PersonaScriptDto[];
  setId: string;
  tenantId: string;
  canEdit: boolean;
  sentMeta: SentScriptMeta | null;
  opportunityAngle: string;
  sheetTitle: string;
  setName: string;
}) {
  const { t, i18n } = useTranslation();
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const discardedCorrelationIds = useRef(new Set<string>());
  const [flash, setFlash] = useState<{ key: string; source: "manual" | "ai" } | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [aiEditKey, setAiEditKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  /** `null` is the current version; older versions show only their scene table. */
  const [historyVersion, setHistoryVersion] = useState<number | null>(null);
  const isViewingCurrent = historyVersion == null;
  const [hookPanel, setHookPanel] = useState<{
    correlationId: string;
    variants: PersonaHookVariationSuggestion[];
  } | null>(null);

  const suggestHooks = useSuggestPersonaHookVariations({
    onCompleted: (completed) => {
      if (!shouldOpenHookVariationsPanel(completed.correlationId, discardedCorrelationIds.current)) {
        return;
      }
      if (completed.parentScriptId !== script.id) return;
      setHookPanel({ correlationId: completed.correlationId, variants: completed.variants });
    },
  });
  const confirmHooks = useConfirmPersonaHookVariations();
  const patchContent = usePatchPersonaScriptContent();
  const aiEdit = useAiEditPersonaScripts();
  const toggleFavorite = useTogglePersonaScriptFavorite();
  const openOrder = useOpenMarketplaceOrder();
  const { linkCopied, copied, share, copy } = usePersonaScriptClipboard(script);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const flashCell = useCallback((key: string, source: "manual" | "ai" = "manual") => {
    setFlash({ key, source });
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), PERSONA_SCRIPT_CELL_FLASH_MS);
  }, []);

  const saveContent = useCallback(
    (key: string, patch: Parameters<typeof patchContent.mutate>[0]["patch"]) => {
      setSavingKey(key);
      patchContent.mutate(
        { setId, scriptId: script.id, tenantId, patch },
        {
          onSuccess: () => flashCell(key),
          onSettled: () => setSavingKey(null),
        },
      );
    },
    [flashCell, patchContent, script.id, setId, tenantId],
  );

  const untitled = t("personaOpportunities.scripts.untitledHook", {
    defaultValue: "Script {{slot}}",
    slot: script.slotIndex,
  });
  const title = getPersonaScriptDisplayTitle(script, untitled);
  const variantMeta = getHookVariantDisplayMeta(script, siblings);
  const isHookVariant = isPersonaScriptHookVariant(script);
  const createdAtLabel = formatScriptCreatedAt(script.createdAt, i18n.language);
  const orderHref = sentMeta
    ? marketplaceOrderHref(sentMeta.orderId, sentMeta.orderScriptId)
    : undefined;

  const isAiEditing =
    aiEdit.isPending &&
    !aiEdit.variables?.cell &&
    (aiEdit.variables?.scriptIds.includes(script.id) ?? false);
  const showAddThreeHooks =
    hookPanel == null &&
    canShowAddThreeHooks({
      canGenerateScripts: canEdit,
      script,
      siblings,
      isSent: script.isHookSetLocked === true,
    });
  const isSuggestingHooks =
    suggestHooks.isPending && suggestHooks.variables?.scriptId === script.id;

  const scriptHeading = (
    <div className="mb-4 flex flex-col gap-3">
      {sentMeta ? <ScriptSentToVendorCard sentMeta={sentMeta} className="mt-0 mb-2" /> : null}
      <div className="flex flex-wrap items-center gap-2">
        {canEdit ? (
          <PersonaScriptTitleEditor
            title={title}
            canEdit
            titleClassName="text-drawer-title"
            className="-ml-2"
            isEditing={editingKey === "title"}
            isFlashing={flash?.key === "title"}
            isSaving={savingKey === "title"}
            onStartEdit={() => {
              setAiEditKey(null);
              setEditingKey("title");
            }}
            onStopEdit={() => setEditingKey((current) => (current === "title" ? null : current))}
            onSave={(next) => saveContent("title", { target: "hook", value: next })}
          />
        ) : (
          <p className="text-drawer-title text-foreground">{title}</p>
        )}
        <ScriptPlatformBadge format={script.youtubeFormat} />
        {isSentToAnyPartner(sentMeta) ? (
          <Badge variant="status-sent" iconStart="check">
            {t("personaOpportunities.scripts.sentToProductionBadge", "Sent to production")}
          </Badge>
        ) : null}
        {script.storyboardStatus === "READY" ? (
          <Badge variant="outline">
            {t("opportunities.scripts.storyboardBadge.sketch", "Sketch")}
          </Badge>
        ) : null}
        {script.storyboardPanelStatus === "READY" ? (
          <Badge variant="outline">
            {t("opportunities.scripts.storyboardBadge.animatic", "Animatic")}
          </Badge>
        ) : null}
      </div>
      {isHookVariant ? (
        <Badge variant="ai" iconStart="sparkles" className="w-fit whitespace-normal text-left">
          {script.hookStrategy?.trim()
            ? t("personaOpportunities.scripts.hookVariantBadge", {
                defaultValue: "{{strategy}} · HOOK VARIANT OF S{{slot}}",
                strategy: script.hookStrategy.trim(),
                slot: variantMeta?.parentSlot ?? script.slotIndex,
              })
            : t("personaOpportunities.scripts.hookVariantBadgeFallback", {
                defaultValue: "HOOK VARIANT OF S{{slot}}",
                slot: variantMeta?.parentSlot ?? script.slotIndex,
              })}
        </Badge>
      ) : null}
      <p className="flex flex-wrap items-center gap-x-2 text-caption tabular-nums text-muted-foreground">
        <span>
          {scriptMetaLine(
            {
              slotIndex: script.slotIndex,
              sceneCount: script.scenes.length,
              duration: script.duration,
            },
            t,
            variantMeta,
          )}
        </span>
        {createdAtLabel ? (
          <span className="inline-flex items-center gap-1">
            <Icon name="clock" size="xs" color="muted" aria-hidden />
            {createdAtLabel}
          </span>
        ) : null}
      </p>
      <ScriptBroadcastSource source={script.broadcastSource} className="text-caption" />
      {isCustomAssetScript(script) ? (
        <ScriptCustomAssetRowLink script={script} setId={setId} scope="persona-opportunities" />
      ) : null}
    </div>
  );

  return (
    <>
      <SheetHeader className="shrink-0 gap-0 px-6 py-0">
        <OpportunityHubDrawerToolbar
          sheetTitle={sheetTitle}
          setName={setName}
          isFavorite={script.isFavorite}
          onToggleFavorite={() =>
            toggleFavorite.mutate({
              setId,
              scriptId: script.id,
              tenantId,
              isFavorite: !script.isFavorite,
            })
          }
          copied={copied}
          onCopy={() => void copy()}
          exportDocument={{
            part: {
              kind: "script",
              script,
              untitled,
              labels: scriptDocxLabels(t),
            },
          }}
          linkCopied={linkCopied}
          onShare={() => void share()}
          extraActions={
            showAddThreeHooks ? (
              <PersonaScriptAddThreeHooksButton
                isSuggesting={isSuggestingHooks}
                onAddThreeHooks={() => suggestHooks.mutate({ setId, scriptId: script.id, tenantId })}
              />
            ) : null
          }
          isAiEditing={isAiEditing}
          onAiEdit={() => {
            setEditingKey(null);
            setAiEditKey(null);
            setAiEditOpen(true);
          }}
          sentMeta={sentMeta}
          onSendToVendor={() => setSendOpen(true)}
          orderHref={orderHref}
          onOpenOrder={openOrder}
          hideActions={!isViewingCurrent}
        />
        <PersonaScriptBusyStatus
          isSuggesting={isSuggestingHooks}
          isAiEditing={isAiEditing}
          className="pb-3"
        />
      </SheetHeader>
      <SheetBody className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden px-0 py-0">
      <ScriptsHistoryPanel
        enabled
        setId={setId}
        tenantId={tenantId}
        script={script}
        canRestore={canEdit}
        surface="drawer"
        hideCurrentConcept
        selection={{ version: historyVersion, onChange: setHistoryVersion }}
        intro={(view) =>
          // `view.title` falls back to the live script until the snapshot query settles.
          view.isLatest ? (
            scriptHeading
          ) : view.preview ? (
            <PastVersionHeading
              preview={view.preview}
              siblings={siblings}
              createdAt={view.versionCreatedAt}
            />
          ) : null
        }
        leading={(view, scenePreview) => view.isLatest ? (
      <PersonaScriptExpandedBody
        script={script}
        setId={setId}
        tenantId={tenantId}
        searchQuery=""
        isSelected={false}
        canEdit={canEdit}
        canAddAlternateHooks={!siblings.some((sibling) => sibling.parentScriptId === script.id)}
        originalBriefUrl={resolveOriginalBriefUrl(script)}
        editingKey={editingKey !== "title" ? editingKey : null}
        aiEditKey={aiEditKey}
        flashKey={flash && flash.key !== "title" ? flash.key : null}
        flashSource={flash?.source ?? "manual"}
        savingKey={savingKey !== "title" ? savingKey : null}
        aiBusyKey={
          aiEdit.isPending && aiEdit.variables?.cell && aiEditKey ? aiEditKey : null
        }
        onStartEdit={(key) => {
          setAiEditKey(null);
          setEditingKey(key);
        }}
        onStopEdit={() => setEditingKey((current) => (current !== "title" ? null : current))}
        onStartAi={(key) => {
          setEditingKey(null);
          setAiEditOpen(false);
          setAiEditKey(key);
        }}
        onStopAi={() => setAiEditKey(null)}
        onApplyAi={(key, instruction) => {
          const cell = parsePersonaScriptSceneCellKey(key);
          if (!cell) return;
          aiEdit.mutate(
            { setId, scriptIds: [script.id], instruction, tenantId, cell },
            {
              onSuccess: () => flashCell(key, "ai"),
              onSettled: () => setAiEditKey((current) => (current === key ? null : current)),
            },
          );
        }}
        onSaveCell={(sceneNumber, field: PersonaScriptSceneField, value) =>
          saveContent(`${sceneNumber}:${field}`, {
            target: "scene",
            sceneNumber,
            field,
            value,
          })
        }
        hookCompare={
          hookPanel
            ? {
                variants: hookPanel.variants,
                isConfirming: confirmHooks.isPending,
                onConfirm: (keptIndexes) => {
                  confirmHooks.mutate(
                    {
                      setId,
                      scriptId: script.id,
                      tenantId,
                      correlationId: hookPanel.correlationId,
                      parentScriptId: script.id,
                      keptIndexes,
                    },
                    { onSuccess: () => setHookPanel(null) },
                  );
                },
                onDiscard: () => {
                  discardedCorrelationIds.current.add(hookPanel.correlationId);
                  setHookPanel(null);
                },
              }
            : null
        }
        showAddThreeHooks={showAddThreeHooks}
        isSuggesting={isSuggestingHooks}
        onAddThreeHooks={() => suggestHooks.mutate({ setId, scriptId: script.id, tenantId })}
        onAiEdit={() => {
          setEditingKey(null);
          setAiEditKey(null);
          setAiEditOpen(true);
        }}
        onSendToVendor={() => setSendOpen(true)}
        isAiEditing={isAiEditing}
        sentMeta={sentMeta}
        orderHref={orderHref}
        onOpenOrder={openOrder}
        actionsPlacement="header"
        surface="drawer"
        storyboardPlacement="above-description"
        scenePreview={scenePreview}
        changedCellKeys={changedSceneCellKeys(script.scenes, view.original)}
      />
        ) : scenePreview}
      />
      </SheetBody>
      {aiEditOpen ? (
        <PersonaAiScriptEditorModal
          open
          hook={script.hook}
          concept={script.concept}
          opportunityTitle={opportunityAngle || null}
          isSubmitting={aiEdit.isPending}
          onClose={() => setAiEditOpen(false)}
          onSubmit={(instruction) => {
            aiEdit.mutate(
              { setId, scriptIds: [script.id], instruction, tenantId },
              { onSuccess: () => setAiEditOpen(false) },
            );
          }}
        />
      ) : null}
      {sendOpen ? (
        <PersonaSendToVendorDialog
          open
          onOpenChange={setSendOpen}
          scriptIds={[script.id]}
          scriptTitlesById={{ [script.id]: title }}
          scriptHooksById={{ [script.id]: script.hooks ?? [] }}
          scriptDetailsById={{ [script.id]: personaScriptReferenceDetails(script) }}
          sentScriptsMap={sentMeta ? { [script.id]: sentMeta } : {}}
          tenantId={tenantId}
          opportunitySetId={setId}
        />
      ) : null}
    </>
  );
}

export interface OpportunityHubScriptDrawerProps {
  row: HubChromeScriptRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string | null;
  canEdit: boolean;
  sentScripts: SentScriptsMap;
}

export function OpportunityHubScriptDrawer({
  row,
  open,
  onOpenChange,
  tenantId,
  canEdit,
  sentScripts,
}: OpportunityHubScriptDrawerProps) {
  const { t } = useTranslation();
  const scriptsQuery = useQuery(
    personaOpportunitySetScriptsQueryOptions(tenantId, row?.setId ?? "", {
      enabled: open && row?.kind === "script" && !!tenantId && !!row.setId,
    }),
  );
  const briefsQuery = useQuery(
    personaOpportunitySetBriefsQueryOptions(tenantId, row?.setId ?? "", {
      enabled: open && row?.kind === "brief" && !!tenantId && !!row.setId,
    }),
  );

  const script =
    row?.kind === "script"
      ? (scriptsQuery.data?.find((item) => item.id === row.id) ?? null)
      : null;
  const brief =
    row?.kind === "brief"
      ? (briefsQuery.data?.find((item) => item.id === row.id) ?? null)
      : null;
  const activeQuery = row?.kind === "brief" ? briefsQuery : scriptsQuery;
  const isLoading = open && !!tenantId && activeQuery.isLoading;
  const isMissing = open && !!tenantId && activeQuery.isSuccess && (row?.kind === "brief" ? !brief : !script);
  const siblings =
    script == null
      ? []
      : (scriptsQuery.data ?? []).filter(
          (item) => item.personaOpportunityId === script.personaOpportunityId,
        );
  const sentMeta = row ? (sentScripts[row.id] ?? null) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        closeClassName={HEADER_CLOSE_CLASS}
        className={cn(
          "flex w-full flex-col overflow-hidden bg-card p-0 sm:max-w-[1200px] 2xl:max-w-[1400px]",
          HUB_DRAWER_DOCKED_CLASS,
        )}
        onPointerDownOutside={(event) => {
          if (shouldKeepHubDrawerOpen(event.target)) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (shouldKeepHubDrawerOpen(event.target)) event.preventDefault();
        }}
      >
        {row ? (
          script && tenantId ? (
            <HubScriptDocument
              key={script.id}
              script={script}
              siblings={siblings}
              setId={row.setId}
              tenantId={tenantId}
              canEdit={canEdit}
              sentMeta={sentMeta}
              opportunityAngle={row.opportunityAngle}
              sheetTitle={row.title}
              setName={row.setName}
            />
          ) : brief && tenantId ? (
            <OpportunityHubBriefDocument
              key={brief.id}
              brief={brief}
              setId={row.setId}
              tenantId={tenantId}
              canEdit={canEdit}
              sentMeta={sentMeta}
              opportunityAngle={row.opportunityAngle}
              sheetTitle={row.title}
              setName={row.setName}
            />
          ) : (
            <>
              <SheetHeader className="shrink-0 gap-0 px-6 py-0">
                <div className="flex min-h-16 items-center pl-14">
                  <SheetTitle className="break-words text-base leading-snug">{row.title}</SheetTitle>
                  <SheetDescription className="sr-only">{row.setName}</SheetDescription>
                </div>
              </SheetHeader>
              <SheetBody className="flex min-h-0 flex-col gap-4">
                {isLoading ? (
                  <div className="flex flex-col gap-3" aria-busy="true">
                    <Skeleton className="h-6 w-2/3" />
                    <Skeleton className="h-24 w-full" />
                    <span className="sr-only">
                      {t("personaOpportunities.hub.scripts.loading", "Loading script")}
                    </span>
                  </div>
                ) : null}
                {isMissing ? (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "personaOpportunities.hub.scripts.notFound",
                      "This script or brief is no longer in the set.",
                    )}
                  </p>
                ) : null}
              </SheetBody>
            </>
          )
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
